import {useCallback, useEffect, useReducer} from "react";
import {createBackup, getActiveCreationJob, getCreationJob} from "@/api/BackupApi.ts";
import {BackupCreationProgressEvent} from "@/backup/RestoreProgress.ts";
import {useBackupCreationRealtime} from "@/backup/useBackupRealtime.ts";

interface State {
    event: BackupCreationProgressEvent | null;
    starting: boolean;
    error: string | null;
    handled: boolean
}

type Action = { type: "START" } | { type: "EVENT"; event: BackupCreationProgressEvent } | {
    type: "ERROR";
    error: string
} | { type: "STATUS_FAILED"; error: string } | { type: "HANDLED" };
const initial: State = {event: null, starting: false, error: null, handled: false};

function reducer(state: State, action: Action): State {
    if (action.type === "START") return {...state, event: null, starting: true, error: null, handled: false};
    if (action.type === "ERROR") return {...state, starting: false, error: action.error};
    if (action.type === "STATUS_FAILED") return state.error === action.error ? state : {...state, error: action.error};
    if (action.type === "HANDLED") return {...state, handled: true};
    if (state.event?.jobId === action.event.jobId && action.event.revision <= state.event.revision) {
        return state.error && !action.event.failed ? {...state, error: null} : state;
    }
    return {
        event: action.event,
        starting: false,
        error: action.event.failed ? action.event.error ?? "Die Backup-Erstellung ist fehlgeschlagen." : null,
        handled: false
    };
}

export function useCreationJob(onStarted: (event: BackupCreationProgressEvent) => void, onFinished: (event: BackupCreationProgressEvent) => void) {
    const [state, dispatch] = useReducer(reducer, initial);
    const event = state.event;
    const jobId = event?.jobId;
    const finished = event?.finished;
    const handleProgress = useCallback(
        (value: BackupCreationProgressEvent) => dispatch({type: "EVENT", event: value}),
        [],
    );
    useBackupCreationRealtime(finished ? undefined : jobId, handleProgress);
    useEffect(() => {
        let cancelled = false;
        getActiveCreationJob().then(job => {
            if (!cancelled && job) {
                dispatch({type: "EVENT", event: job});
                onStarted(job)
            }
        }).catch(() => {
            if (!cancelled) dispatch({type: "STATUS_FAILED", error: "Der aktive Erstellungsstatus konnte nicht geladen werden."});
        });
        return () => {
            cancelled = true
        }
    }, [onStarted]);
    useEffect(() => {
        if (!jobId || finished) return;
        const timer = window.setInterval(() => void getCreationJob(jobId).then(value => dispatch({
            type: "EVENT",
            event: value
        })).catch(() => dispatch({type: "STATUS_FAILED",
            error: "Der Erstellungsstatus konnte nicht aktualisiert werden. Ein neuer Versuch läuft."})), 2000);
        return () => window.clearInterval(timer)
    }, [finished, jobId]);
    useEffect(() => {
        if (!event?.finished || state.handled) return;
        dispatch({type: "HANDLED"});
        onFinished(event)
    }, [event, onFinished, state.handled]);

    async function start() {
        if (state.starting || (event && !event.finished)) return;
        dispatch({type: "START"});
        try {
            const value = await createBackup();
            dispatch({type: "EVENT", event: value});
            onStarted(value)
        } catch {
            dispatch({type: "ERROR", error: "Die Backup-Erstellung konnte nicht gestartet werden."})
        }
    }

    return {event, error: state.error, busy: state.starting || Boolean(event && !event.finished), start};
}
